import React, { useEffect, useState, useCallback, useRef } from "react";
import { LiveKitRoom, RoomAudioRenderer, VideoTrack, useTracks, useLocalParticipant, useRoomContext } from "@livekit/components-react";
import { Track, RoomEvent } from "livekit-client";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Video, VideoOff, Mic, MicOff, Radio, Volume2 } from "lucide-react";

function ViewerTracks() {
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: false }], { onlySubscribed: true });
  const remote = tracks.filter(t => !t.participant.isLocal);
  if (!remote.length) {
    return (
      <div className="w-full h-full flex items-center justify-center text-white/60">
        <div className="text-center p-6">
          <Radio size={36} className="mx-auto mb-2 animate-pulse"/>
          <p className="font-bold text-sm">L'hôte prépare sa caméra…</p>
        </div>
      </div>
    );
  }
  return <VideoTrack trackRef={remote[0]} className="w-full h-full object-cover" data-testid="live-video-track"/>;
}

function DataBridge() {
  // Canal de données LiveKit (T2) → poke de resynchronisation immédiate (rejeu since_seq côté page)
  const room = useRoomContext();
  useEffect(() => {
    const onData = () => window.dispatchEvent(new CustomEvent("fripri-live-event"));
    room.on(RoomEvent.DataReceived, onData);
    return () => { room.off(RoomEvent.DataReceived, onData); };
  }, [room]);
  return null;
}

function SoundUnlock() {
  const room = useRoomContext();
  const [needsUnlock, setNeedsUnlock] = useState(false);
  useEffect(() => {
    const check = () => setNeedsUnlock(!room.canPlaybackAudio);
    room.on("audioPlaybackChanged", check);
    check();
    return () => { room.off("audioPlaybackChanged", check); };
  }, [room]);
  if (!needsUnlock) return null;
  return (
    <button onClick={() => room.startAudio().catch(()=>{})} className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 btn btn-coral btn-sm inline-flex items-center gap-1.5" data-testid="unlock-audio-btn">
      <Volume2 size={14}/> Activer le son
    </button>
  );
}

function HostControls({ sessionId }) {
  const { localParticipant } = useLocalParticipant();
  const [cam, setCam] = useState(false);
  const [mic, setMic] = useState(false);
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);

  const startRecording = useCallback(() => {
    try {
      if (recRef.current) return;
      const camTrack = localParticipant.getTrackPublication(Track.Source.Camera)?.track?.mediaStreamTrack;
      const micTrack = localParticipant.getTrackPublication(Track.Source.Microphone)?.track?.mediaStreamTrack;
      const tracks = [camTrack, micTrack].filter(Boolean);
      if (!tracks.length || typeof MediaRecorder === "undefined") return;
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";
      if (!MediaRecorder.isTypeSupported(mime)) return;
      const rec = new MediaRecorder(new MediaStream(tracks), { mimeType: mime, videoBitsPerSecond: 1200000, audioBitsPerSecond: 96000 });
      rec.ondataavailable = async (e) => {
        if (!e.data || !e.data.size) return;
        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live/sessions/${sessionId}/recording/chunk`, {
            method: "POST", credentials: "include", body: e.data,
          });
        } catch { /* segment perdu, le suivant reprend */ }
      };
      rec.start(10000);
      recRef.current = rec;
      setRecording(true);
    } catch { /* enregistrement indisponible, le live continue */ }
  }, [localParticipant, sessionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await localParticipant.setCameraEnabled(true);
        await localParticipant.setMicrophoneEnabled(true);
        if (!cancelled) { setCam(true); setMic(true); startRecording(); }
      } catch {
        toast.error("Autorisez l'accès à la caméra et au micro dans votre navigateur");
      }
    })();
    return () => {
      cancelled = true;
      try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch {}
      recRef.current = null;
    };
  }, [localParticipant, startRecording]);

  const localTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: false }], { onlySubscribed: false });
  const mine = localTracks.find(t => t.participant.isLocal);

  const toggleCam = async () => {
    try { await localParticipant.setCameraEnabled(!cam); setCam(!cam); } catch {}
  };
  const toggleMic = async () => {
    try { await localParticipant.setMicrophoneEnabled(!mic); setMic(!mic); } catch {}
  };

  return (
    <>
      {mine ? (
        <VideoTrack trackRef={mine} className="w-full h-full object-cover" data-testid="host-video-preview"/>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/60 text-sm font-bold">Démarrage de la caméra…</div>
      )}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {recording && <span className="pill pill-coral inline-flex items-center gap-1 text-[10px]" data-testid="recording-badge">● REC</span>}
        <button onClick={toggleCam} className={`w-11 h-11 border-2 border-white flex items-center justify-center ${cam ? "bg-white text-black" : "bg-[color:var(--color-coral)] text-white"}`} title={cam ? "Couper la caméra" : "Activer la caméra"} data-testid="toggle-cam-btn">
          {cam ? <Video size={18}/> : <VideoOff size={18}/>}
        </button>
        <button onClick={toggleMic} className={`w-11 h-11 border-2 border-white flex items-center justify-center ${mic ? "bg-white text-black" : "bg-[color:var(--color-coral)] text-white"}`} title={mic ? "Couper le micro" : "Activer le micro"} data-testid="toggle-mic-btn">
          {mic ? <Mic size={18}/> : <MicOff size={18}/>}
        </button>
      </div>
    </>
  );
}

export const LiveVideo = ({ sessionId, isHost, active }) => {
  const [auth, setAuth] = useState(null);
  const [error, setError] = useState(null);

  const fetchToken = useCallback(() => {
    api.post(`/live/sessions/${sessionId}/video-token`)
      .then(r => setAuth(r.data))
      .catch(e => {
        if (e.response?.status === 401) setError("login");
        else setError(e.response?.data?.detail || "Vidéo indisponible");
      });
  }, [sessionId]);

  useEffect(() => { if (active) fetchToken(); }, [active, fetchToken]);

  if (!active) return null;
  if (error === "login") {
    return (
      <div className="w-full h-full flex items-center justify-center text-white p-6 text-center">
        <div>
          <Radio size={32} className="mx-auto mb-3 text-[color:var(--color-coral)]"/>
          <p className="font-bold text-sm mb-3">Connecte-toi pour regarder le direct</p>
          <a href="/login" className="btn btn-coral btn-sm" data-testid="live-login-cta">Se connecter</a>
        </div>
      </div>
    );
  }
  if (error) return <div className="w-full h-full flex items-center justify-center text-white/60 text-sm font-bold p-6 text-center">{error}</div>;
  if (!auth) return <div className="w-full h-full flex items-center justify-center text-white/60 text-sm font-bold">Connexion au direct…</div>;

  return (
    <LiveKitRoom
      token={auth.token}
      serverUrl={auth.server_url}
      connect
      audio={false}
      video={false}
      options={{ adaptiveStream: true, dynacast: true }}
      onError={() => setError("Connexion vidéo interrompue — rechargez la page")}
      className="w-full h-full relative"
      data-testid="livekit-room"
    >
      {auth.is_publisher && isHost ? <HostControls sessionId={sessionId}/> : <ViewerTracks/>}
      <RoomAudioRenderer/>
      <SoundUnlock/>
      <DataBridge/>
    </LiveKitRoom>
  );
};

export default LiveVideo;
